import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
};

export const companiesAPI = {
    getAll: () => api.get('/companies'),
    getOne: (id) => api.get(`/companies/${id}`),
    create: (data) => api.post('/companies', data),
    update: (id, data) => api.put(`/companies/${id}`, data),
    delete: (id) => api.delete(`/companies/${id}`),
};

export const usersAPI = {
    getAll: () => api.get('/users'),
    getTechnicians: () => api.get('/users/technicians'),
    create: (data) => api.post('/users/create', data),
    delete: (id) => api.delete(`/users/${id}`),
};

export const assetsAPI = {
    getAll: (params) => api.get('/assets', { params }),
    getOne: (id) => api.get(`/assets/${id}`),
    create: (data) => api.post('/assets', data),
    update: (id, data) => api.put(`/assets/${id}`, data),
    delete: (id) => api.delete(`/assets/${id}`),
};

export const ticketsAPI = {
    getAll: (params) => api.get('/tickets', { params }),
    getOne: (id) => api.get(`/tickets/${id}`),
    create: (data) => api.post('/tickets', data),
    update: (id, data) => api.put(`/tickets/${id}`, data),
    delete: (id) => api.delete(`/tickets/${id}`),
};

export const serviceFormsAPI = {
    getAll: (params) => api.get('/service-forms', { params }),
    create: (data) => api.post('/service-forms', data),
    sign: (id, signature) => {
        const formData = new FormData();
        formData.append('signature', signature);
        return api.put(`/service-forms/${id}/sign`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

export const proposalsAPI = {
    getAll: (params) => api.get('/proposals', { params }),
    getOne: (id) => api.get(`/proposals/${id}`),
    create: (data) => api.post('/proposals', data),
    update: (id, data) => api.put(`/proposals/${id}`, data),
    delete: (id) => api.delete(`/proposals/${id}`),
};

export const notificationsAPI = {
    getAll: () => api.get('/notifications'),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
};

export const inventoryAPI = {
    getAll: (params) => api.get('/inventory', { params }),
    getOne: (id) => api.get(`/inventory/${id}`),
    create: (data) => api.post('/inventory', data),
    update: (id, data) => api.put(`/inventory/${id}`, data),
    delete: (id) => api.delete(`/inventory/${id}`),
};

export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
};

export const crmAPI = {
    getRequests: (params) => api.get('/crm/requests', { params }),
    getOne: (id) => api.get(`/crm/requests/${id}`),
    create: (data) => api.post('/crm/requests', data),
    update: (id, data) => api.put(`/crm/requests/${id}`, data),
    delete: (id) => api.delete(`/crm/requests/${id}`),
    getStats: (period) => api.get('/crm/stats', { params: { period } }),
    translate: (text) => {
        const formData = new FormData();
        formData.append('text', text);
        return api.post('/crm/translate', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

export const uploadAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getFile: (id) => api.get(`/files/${id}`),
};

export default api;
